import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, Button, Input, message, Modal } from 'antd';
import { PlusOutlined, LinkOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { getBase64 } from '@/constant/reusabaleFunctions';
import { uploadFile } from '@/server/server';
import apiClient from '@/server/server';
import imageCompression from 'browser-image-compression';

// Debounce function to prevent rapid onChange calls
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  
  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

const StoryNewsMediaInput = ({ value = [], onChange, maxCount = 6, onFilesReady, acceptedTypes = ['image/*'] }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [isUrlModalVisible, setIsUrlModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Store files that need to be uploaded (not uploaded yet)
  const [pendingFiles, setPendingFiles] = useState([]);
  const pendingFilesRef = useRef([]);
  const [uploadRefSent, setUploadRefSent] = useState(false);
  const previousValueRef = useRef([]);
  const [isUploading, setIsUploading] = useState(false); // Add upload state flag
  
  // Add flag to prevent onChange calls during external value updates
  const isUpdatingFromExternalValue = useRef(false);
  
  // Debounced onChange to prevent rapid calls
  const debouncedOnChange = useDebounce((urls) => {
    // Don't call onChange if we're updating from external value
    if (isUpdatingFromExternalValue.current) {
      return;
    }
    
    // Filter out preview URLs and remove duplicates
    const validUrls = urls.filter(url => 
      typeof url === 'string' && 
      !url.startsWith('data:') && 
      !url.startsWith('blob:') &&
      url.trim().length > 0
    );
    const uniqueUrls = [...new Set(validUrls)];
    
    // Only call onChange if the URLs have actually changed
    const currentValueString = JSON.stringify([...new Set(value || [])]);
    const newValueString = JSON.stringify(uniqueUrls);
    
    if (currentValueString !== newValueString) {
      onChange(uniqueUrls);
    }
  }, 100);

  // Custom beforeUpload function that handles different file types
  const beforeUpload = (file) => {
    console.log('🎬 [StoryNewsMediaInput] beforeUpload called with file:', {
      name: file.name,
      type: file.type,
      size: (file.size / 1024 / 1024).toFixed(2) + 'MB',
      acceptedTypes: acceptedTypes
    });

    // Check if file type is accepted
    const isAcceptedType = acceptedTypes.some(type => {
      if (type === 'image/*') {
        return file.type.startsWith('image/');
      } else if (type === 'video/*') {
        return file.type.startsWith('video/');
      } else if (type === '*/*') {
        return true;
      } else {
        return file.type === type;
      }
    });

    console.log('🎬 [StoryNewsMediaInput] File type check:', {
      fileType: file.type,
      isAcceptedType: isAcceptedType
    });

    if (!isAcceptedType) {
      const acceptedTypesText = acceptedTypes.map(type => {
        if (type === 'image/*') return 'images';
        if (type === 'video/*') return 'videos';
        if (type === '*/*') return 'any files';
        return type;
      }).join(', ');
      message.error(`You can only upload ${acceptedTypesText}!`);
      console.log('❌ [StoryNewsMediaInput] File type rejected');
      return false;
    }

    // Check file size - different limits for different file types
    let maxSize = 2; // Default 2MB for images
    if (file.type.startsWith('video/')) {
      maxSize = 100; // 100MB for videos
    } else if (acceptedTypes.includes('*/*')) {
      maxSize = 50; // 50MB for any files
    }

    console.log('🎬 [StoryNewsMediaInput] Size check:', {
      fileSize: (file.size / 1024 / 1024).toFixed(2) + 'MB',
      maxSize: maxSize + 'MB'
    });

    const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
    if (!isLtMaxSize) {
      message.error(`File must be smaller than ${maxSize}MB!`);
      console.log('❌ [StoryNewsMediaInput] File size rejected');
      return false;
    }

    console.log('✅ [StoryNewsMediaInput] File passed validation');
    return false; // IMPORTANT: Return false to prevent automatic upload
  };

  // Keep ref updated with current pendingFiles
  useEffect(() => {
    pendingFilesRef.current = pendingFiles;
  }, [pendingFiles]);

  // Sync with external value prop - with proper dependency management
  useEffect(() => {
    // Only update if value has actually changed
    const valueString = JSON.stringify(value);
    const previousValueString = JSON.stringify(previousValueRef.current);
    
    if (valueString !== previousValueString) {
      previousValueRef.current = value;
      
      // Set flag to prevent onChange calls during this update
      isUpdatingFromExternalValue.current = true;
      
      if (Array.isArray(value) && value.length > 0) {
        // Filter out invalid URLs (preview URLs, empty strings, etc.)
        const validUrls = value.filter(item => 
          typeof item === 'string' && 
          !item.startsWith('data:') && 
          !item.startsWith('blob:') &&
          item.trim().length > 0
        );
        
        // Convert URLs to file objects for the Upload component
        const newFileList = validUrls.map((item, index) => {
          if (typeof item === 'string') {
            // Item is a URL string (already uploaded)
            return {
              uid: `url-${index}-${item.substring(item.lastIndexOf('/') + 1)}`,
              name: item.substring(item.lastIndexOf('/') + 1) || `media-${index}`,
              status: 'done',
              url: item
            };
          } else if (item && item.url) {
            // Item is already a file object
            return {
              ...item,
              uid: item.uid || `file-${index}-${item.url?.substring(item.url.lastIndexOf('/') + 1)}`
            };
          }
          return null;
        }).filter(Boolean);
        
        // Only update fileList if it's actually different
        const currentFileListString = JSON.stringify(fileList.map(f => ({ uid: f.uid, url: f.url })));
        const newFileListString = JSON.stringify(newFileList.map(f => ({ uid: f.uid, url: f.url })));
        
        if (currentFileListString !== newFileListString) {
          setFileList(newFileList);
        }
      } else if (fileList.length > 0) {
        setFileList([]);
      }
      
      // Reset flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isUpdatingFromExternalValue.current = false;
      }, 50);
    }
  }, [value]); // Removed fileList from dependencies to prevent infinite loops

  // Expose upload function to parent component - only once
  useEffect(() => {
    if (onFilesReady && typeof onFilesReady === 'function' && !uploadRefSent) {
      onFilesReady({
        hasPendingFiles: () => pendingFilesRef.current.length > 0,
        uploadPendingFiles: async () => {
          // Prevent multiple simultaneous uploads
          if (isUploading) {
            return [];
          }
          
          // Get current pending files from ref
          const currentPendingFiles = pendingFilesRef.current;
          
          if (currentPendingFiles.length === 0) {
            return [];
          }
          setIsUploading(true);
          setUploading(true);

          try {
            const uploadPromises = currentPendingFiles.map(async (fileData) => {
              if (fileData.type === 'file') {
                // Upload actual file
                return await uploadFile(fileData.file);
              } else if (fileData.type === 'url') {
                // Upload from URL
                const response = await apiClient.post('/uploads/from-url', {
                  imageUrl: fileData.url
                });
                return response.data.data.url;
              }
            });

            const uploadedUrls = await Promise.all(uploadPromises);
            
            // Filter out invalid URLs and remove duplicates
            const validUploadedUrls = uploadedUrls
              .filter(url => url && typeof url === 'string' && url.trim().length > 0)
              .map(url => url.trim());
            
            const uniqueUploadedUrls = [...new Set(validUploadedUrls)];
            
            // Clear pending files after successful upload
            setPendingFiles([]);
            pendingFilesRef.current = []; // Also clear the ref
            
            // Update fileList to mark files as properly uploaded
            setFileList(prevFileList => 
              prevFileList.map(file => {
                const matchingPendingFile = currentPendingFiles.find(pf => pf.uid === file.uid);
                if (matchingPendingFile) {
                  const uploadedUrl = uniqueUploadedUrls[currentPendingFiles.indexOf(matchingPendingFile)];
                  return {
                    ...file,
                    url: uploadedUrl,
                    status: 'done',
                    preview: undefined // Remove preview since we have the real URL
                  };
                }
                return file;
              })
            );
            
            setUploading(false);
            setIsUploading(false);
            
            console.log('📁 [StoryNewsMediaInput] Uploaded URLs:', uniqueUploadedUrls);
            return uniqueUploadedUrls;
          } catch (error) {
            setUploading(false);
            setIsUploading(false);
            throw error;
          }
        }
      });
      setUploadRefSent(true);
    }
  }, [onFilesReady, uploadRefSent]); // Fixed dependencies

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url?.substring(file.url.lastIndexOf('/') + 1) || 'Preview');
  };

  // Cleanup object URLs when component unmounts or files change
  useEffect(() => {
    return () => {
      fileList.forEach(file => {
        if (file.preview && file.preview.startsWith('blob:')) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [fileList]);

  const handleChange = useCallback(async ({ fileList: newFileList }) => {
    try {
      console.log('🎬 [StoryNewsMediaInput] handleChange called:', {
        newFileListLength: newFileList?.length,
        acceptedTypes: acceptedTypes
      });
      
      if (!Array.isArray(newFileList)) {
        newFileList = [];
      }

      // Process new files that need to be prepared for upload
      const newFiles = newFileList.filter(file => 
        file.originFileObj && file.status !== 'done' && !file.url
      );

      if (newFiles.length > 0) {
        console.log('🎬 [StoryNewsMediaInput] Processing new files:', newFiles.map(f => ({
          name: f.name,
          type: f.originFileObj?.type,
          size: f.originFileObj ? (f.originFileObj.size / 1024 / 1024).toFixed(2) + 'MB' : 'unknown'
        })));

        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };

        // Process files for local storage and preview
        const processedFiles = [...newFileList];
        const newPendingFiles = [];
        
        // Check which files are already in pendingFiles to avoid duplicates
        const existingPendingUids = pendingFiles.map(p => p.uid);
        
        for (let i = 0; i < processedFiles.length; i++) {
          const file = processedFiles[i];
          if (file.originFileObj && !file.url && file.status !== 'done') {
            // Skip if this file is already in pending
            if (existingPendingUids.includes(file.uid)) {
              continue;
            }
            
            try {
              console.log('🎬 [StoryNewsMediaInput] Processing file:', {
                name: file.name,
                type: file.originFileObj.type,
                isVideo: file.originFileObj.type.startsWith('video/')
              });
              
              // Compress if it's an image
              let fileToUpload = file.originFileObj;
              if (file.originFileObj.type.startsWith('image/')) {
                fileToUpload = await imageCompression(file.originFileObj, options);
              }
              
              // Create preview - handle videos differently
              let preview;
              if (file.originFileObj.type.startsWith('video/')) {
                // For videos, create object URL for preview
                preview = URL.createObjectURL(fileToUpload);
              } else {
                // For images, use base64
                preview = await new Promise((resolve) => {
                  getBase64(fileToUpload, resolve);
                });
              }
              
              // Update the file object with preview but don't upload yet
              processedFiles[i] = {
                ...file,
                status: 'done', // Mark as done for UI purposes
                preview: preview,
                url: preview // Use preview as temporary URL for display
              };

              // Add to pending files for later upload (only if not already there)
              newPendingFiles.push({
                type: 'file',
                file: fileToUpload,
                uid: file.uid
              });
              
            } catch (error) {
              processedFiles[i] = {
                ...file,
                status: 'error'
              };
              message.error(`Failed to prepare "${file.name}": ${error.message}`);
            }
          }
        }

        // Update fileList only once after all processing
        setFileList(processedFiles);
        
        // Merge new pending files with existing ones (avoiding duplicates)
        const mergedPendingFiles = [...pendingFiles];
        newPendingFiles.forEach(newFile => {
          if (!mergedPendingFiles.some(f => f.uid === newFile.uid)) {
            mergedPendingFiles.push(newFile);
          }
        });
        setPendingFiles(mergedPendingFiles);

        // Don't call onChange with preview URLs - let the existing URLs remain
        // The actual URLs will be added when the upload completes
        const existingValidUrls = (value || []).filter(url => 
          typeof url === 'string' && 
          !url.startsWith('data:') && 
          !url.startsWith('blob:') &&
          url.trim().length > 0
        );
        
        // Only notify if we have valid URLs to add (not for new file additions)
        // This prevents triggering onChange when files are just being added
        
      } else {
        // Update fileList for UI updates (like removal)
        setFileList(newFileList);
        
        // Handle file removal - extract URLs from existing uploaded files only
        const existingUrls = newFileList
          .filter(file => file.status === 'done' && file.url && !file.url.startsWith('blob:') && !file.url.startsWith('data:')) // Only real URLs, not previews
          .map(file => file.url);
        
        // Remove duplicates and only call onChange if the URLs have actually changed
        const uniqueExistingUrls = [...new Set(existingUrls)];
        const currentValueString = JSON.stringify(value || []);
        const newValueString = JSON.stringify(uniqueExistingUrls);
        
        if (currentValueString !== newValueString) {
          debouncedOnChange(uniqueExistingUrls);
        }

        // Remove from pending files if they were removed
        const remainingPendingFiles = pendingFiles.filter(pendingFile =>
          newFileList.some(file => file.uid === pendingFile.uid)
        );
        setPendingFiles(remainingPendingFiles);
      }
    } catch (error) {
      message.error(`Error processing files: ${error.message}`);
    }
  }, [pendingFiles, value, debouncedOnChange]); // Fixed dependencies

  const handleUrlSubmit = useCallback(async () => {
    if (!urlInput.trim()) {
      message.error('Please enter a URL');
      return;
    }

    try {
      
      const newFile = {
        uid: `url-${Date.now()}`,
        name: urlInput.trim().substring(urlInput.trim().lastIndexOf('/') + 1) || 'media-file',
        status: 'done',
        url: urlInput.trim(), // Display the original URL as preview
      };

      
      // Update fileList with new file
      const updatedFileList = [...fileList, newFile];
      setFileList(updatedFileList);
      
      // Add to pending files for later upload
      setPendingFiles([...pendingFiles, {
        type: 'url',
        url: urlInput.trim(),
        uid: newFile.uid
      }]);
      
      setUrlInput('');
      setIsUrlModalVisible(false);
      message.success('Media added - will be uploaded when you create the story');
      
      // Don't call onChange with the URL immediately - it will be handled after actual upload
      // This prevents preview URLs from being saved to the form
    } catch (error) {
      message.error(`Failed to add URL: ${error.message}`);
    }
  }, [urlInput, fileList, pendingFiles, onChange]); // Removed value from dependencies

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  // Custom request handler to prevent automatic upload
  const customRequest = ({ onSuccess }) => {
    // Immediately call onSuccess to prevent upload attempt
    onSuccess("ok");
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="dashed"
          onClick={() => setIsUrlModalVisible(true)}
          icon={<LinkOutlined />}
          style={{ marginRight: 8 }}
          disabled={uploading}
        >
          Add from URL
        </Button>
        {pendingFiles.length > 0 && (
          <span style={{ color: '#1890ff', fontSize: 12 }}>
            {pendingFiles.length} file(s) ready to upload
          </span>
        )}
        {uploading && <span style={{ color: '#orange', fontSize: 12 }}>Uploading...</span>}
      </div>

      <Upload
        listType="picture-card"
        fileList={fileList}
        onPreview={handlePreview}
        onChange={handleChange}
        beforeUpload={beforeUpload}
        customRequest={customRequest}
        accept={acceptedTypes.includes('video/*') && acceptedTypes.includes('image/*') 
          ? "image/*,video/*,.mp4,.webm,.ogg,.jpg,.jpeg,.png,.gif,.bmp,.webp" 
          : acceptedTypes.join(',')}
        maxCount={maxCount}
        disabled={uploading}
      >
        {fileList.length >= maxCount ? null : uploadButton}
      </Upload>

      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
      >
        {previewImage && previewImage.match(/\.(mp4|webm|ogg|mov|avi)$/i) ? (
          <video controls style={{ width: '100%' }}>
            <source src={previewImage} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
        )}
      </Modal>

      <Modal
        title="Add Media from URL"
        open={isUrlModalVisible}
        onOk={handleUrlSubmit}
        onCancel={() => {
          setIsUrlModalVisible(false);
          setUrlInput('');
        }}
      >
        <Input
          placeholder="Enter media URL (image or video)"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onPressEnter={handleUrlSubmit}
        />
      </Modal>
    </div>
  );
};

export default StoryNewsMediaInput;